import { useState, useEffect, useCallback, useRef } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { organisationsApi } from "../../api/organisations.js";
import { areasApi } from "../../api/areas.js";
import { usersApi } from "../../api/users.js";
import { leadsApi } from "../../api/leads.js";
import { crmApiInstance } from "../../api/client.js";
import {
  Search, X, ShoppingCart, ChevronLeft, ChevronRight,
  Loader2, Pencil, Trash2, Plus, Save, XCircle,
  Building2, MapPin, Phone, FileText, Users, ChevronDown,
  UserPlus,
} from "lucide-react";

// ─── Utilities ────────────────────────────────────────────────────────────────

function useDebounce(val, ms = 350) {
  const [deb, setDeb] = useState(val);
  useEffect(() => {
    const t = setTimeout(() => setDeb(val), ms);
    return () => clearTimeout(t);
  }, [val, ms]);
  return deb;
}

function getRoleName(u) {
  if (!u) return '';
  const r = u.role || u.Role;           // Sequelize may alias as 'Role' (capital)
  if (!r) return '';
  if (typeof r === 'string') return r;
  return r.name || '';
}

function Dash() { return <span style={{ color: "var(--text-muted)" }}>—</span>; }

function fmtDate(iso) {
  if (!iso) return <Dash />;
  return new Date(iso).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
}

function StatusFlag({ flag }) {
  const map = {
    "green ++": { bg: "#d1fae5", color: "#065f46" },
    "green": { bg: "#d1fae5", color: "#065f46" },
    "yellow": { bg: "#fef3c7", color: "#92400e" },
    "red": { bg: "#fee2e2", color: "#991b1b" },
  };
  const s = map[flag] || { bg: "#f1f5f9", color: "#64748b" };
  return (
    <span style={{ background: s.bg, color: s.color, borderRadius: 6, padding: "2px 7px", fontSize: 11, fontWeight: 600 }}>
      {flag || "—"}
    </span>
  );
}

// ─── SearchSelect ─────────────────────────────────────────────────────────────
// options: [{ id, label }]  |  value: string | number | ''
function SearchSelect({ value, onChange, options, placeholder = "— None —", disabled = false, emptyText = "No options" }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const ref = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    const h = e => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  useEffect(() => { if (open) setTimeout(() => inputRef.current?.focus(), 50); }, [open]);

  const filtered = options.filter(o => o.label.toLowerCase().includes(q.toLowerCase()));
  const selected = options.find(o => String(o.id) === String(value));

  const pick = (id) => { onChange(id); setOpen(false); setQ(""); };

  return (
    <div ref={ref} style={{ position: "relative" }}>
      {/* Trigger */}
      <div
        onClick={() => !disabled && setOpen(v => !v)}
        style={{
          border: `1px solid var(--border)`, borderRadius: 8,
          padding: "0 10px 0 12px", height: 38, fontSize: 13,
          cursor: disabled ? "not-allowed" : "pointer",
          background: disabled ? "var(--bg)" : "var(--card)",
          display: "flex", alignItems: "center", justifyContent: "space-between", gap: 6,
          color: selected ? "var(--text-main)" : "var(--text-muted)",
          userSelect: "none",
        }}
      >
        <span style={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {selected ? selected.label : placeholder}
        </span>
        <div style={{ display: "flex", alignItems: "center", gap: 4, flexShrink: 0 }}>
          {value !== "" && value != null && (
            <span
              onClick={e => { e.stopPropagation(); onChange(""); }}
              style={{ cursor: "pointer", color: "var(--text-muted)", lineHeight: 1, fontSize: 16, fontWeight: 400 }}
            >×</span>
          )}
          <ChevronDown size={13} style={{ color: "var(--text-muted)", transform: open ? "rotate(180deg)" : "none", transition: "transform 0.15s" }} />
        </div>
      </div>

      {/* Dropdown */}
      {open && (
        <div style={{
          position: "absolute", top: "calc(100% + 4px)", left: 0, right: 0,
          zIndex: 9999, background: "var(--card)",
          border: "1px solid var(--border)", borderRadius: 10,
          boxShadow: "0 8px 24px rgba(0,0,0,0.16)",
          display: "flex", flexDirection: "column", maxHeight: 240, overflow: "hidden",
        }}>
          <div style={{ padding: "8px 10px", borderBottom: "1px solid var(--border)", flexShrink: 0 }}>
            <input
              ref={inputRef}
              value={q}
              onChange={e => setQ(e.target.value)}
              placeholder="Search…"
              style={{ width: "100%", border: "none", outline: "none", fontSize: 13, background: "transparent", color: "var(--text-main)" }}
            />
          </div>
          <div style={{ overflowY: "auto", flex: 1 }}>
            <div onClick={() => pick("")} style={{ padding: "8px 12px", cursor: "pointer", fontSize: 13, color: "var(--text-muted)", fontStyle: "italic" }}>
              — None —
            </div>
            {filtered.length === 0
              ? <div style={{ padding: "8px 12px", fontSize: 13, color: "var(--text-muted)" }}>{emptyText}</div>
              : filtered.map(o => (
                <div
                  key={o.id}
                  onClick={() => pick(String(o.id))}
                  style={{
                    padding: "8px 12px", cursor: "pointer", fontSize: 13,
                    background: String(o.id) === String(value) ? "rgba(37,99,235,0.08)" : "transparent",
                    fontWeight: String(o.id) === String(value) ? 600 : 400,
                    color: "var(--text-main)",
                  }}
                >
                  {o.label}
                </div>
              ))
            }
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Org Details Modal ────────────────────────────────────────────────────────
function OrgDetailsModal({ org, refreshTrigger, onClose, onAddBranch, onRefresh }) {
  const navigate = useNavigate();
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [leadsPage, setLeadsPage] = useState(1);
  const LEADS_LIMIT = 10;
  const [unlinkingId, setUnlinkingId] = useState(null);

  useEffect(() => {
    organisationsApi.getById(org.id)
      .then(res => setDetail(res.data || res))
      .catch(() => setDetail(org))
      .finally(() => setLoading(false));
  }, [org.id, refreshTrigger]);

  const handleUnlink = async (lead) => {
    if (!window.confirm(`Are you sure you want to unlink "${lead.name}" from this organisation?`)) return;
    setUnlinkingId(lead.id);
    try {
      await leadsApi.update(lead.id, { organisation_id: null });
      if (onRefresh) onRefresh();
    } catch (ex) {
      alert("Failed to unlink customer: " + ex.message);
    } finally {
      setUnlinkingId(null);
    }
  };

  const d = detail || org;
  const leads = d.leads || [];
  const totalLeadsPages = Math.ceil(leads.length / LEADS_LIMIT) || 1;
  const startIndex = (leadsPage - 1) * LEADS_LIMIT;
  const paginatedLeads = leads.slice(startIndex, startIndex + LEADS_LIMIT);

  return createPortal(
    <div style={{
      position: "fixed", inset: 0, zIndex: 1100,
      background: "rgba(15, 23, 42, 0.65)",
      backdropFilter: "blur(4px)",
      display: "flex", alignItems: "center", justifyContent: "center",
      padding: 16,
    }} onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={{
        background: "var(--card)", borderRadius: 16,
        width: "100%", maxWidth: 720, maxHeight: "calc(100vh - 40px)",
        display: "flex", flexDirection: "column",
        boxShadow: "0 24px 64px rgba(0,0,0,0.3)",
        border: "1px solid var(--border)",
        overflow: "hidden"
      }}>
        {/* Header (Static) */}
        <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "18px 20px", borderBottom: "1px solid var(--border)", flexShrink: 0 }}>
          <div style={{ width: 40, height: 40, borderRadius: 10, background: "var(--primary)", color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
            <Building2 size={20} />
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 700, fontSize: 16 }}>{d.name}</div>
            <div style={{ fontSize: 12, color: "var(--text-muted)" }}>Organisation details</div>
          </div>
          <button onClick={onClose} style={{ background: "none", border: "none", cursor: "pointer", color: "var(--text-muted)", padding: 4 }}>
            <XCircle size={22} />
          </button>
        </div>

        {/* Scrollable Content */}
        <div style={{ padding: 20, overflowY: "auto", flex: 1 }}>
          {loading ? (
            <div style={{ display: "flex", alignItems: "center", gap: 8, color: "var(--text-muted)", padding: "24px 0" }}>
              <Loader2 size={16} className="spin" /> Loading…
            </div>
          ) : (
            <>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20, marginBottom: 20 }}>
                <div>
                  <div style={{ marginBottom: 10 }}>
                    {d.is_active !== false
                      ? <span className="badge badge-success" style={{ fontSize: 11 }}>Active</span>
                      : <span className="badge badge-danger" style={{ fontSize: 11 }}>Inactive</span>}
                  </div>
                  {d.phone && (
                    <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13, marginBottom: 6 }}>
                      <Phone size={13} style={{ color: "var(--text-muted)" }} /> {d.phone}
                    </div>
                  )}
                  {d.email && <div style={{ fontSize: 13, marginBottom: 6 }}>{d.email}</div>}
                  {d.gst_number && (
                    <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13 }}>
                      <FileText size={13} style={{ color: "var(--text-muted)" }} />
                      <span style={{ fontWeight: 600, color: "var(--primary)", fontSize: 12 }}>GSTIN</span>
                      <span style={{ fontFamily: "monospace" }}>{d.gst_number}</span>
                    </div>
                  )}
                </div>

                <div>
                  {(d.address_line_1 || d.city || d.state) && (
                    <div style={{ marginBottom: 10 }}>
                      <div style={{ fontSize: 11, fontWeight: 700, color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 4 }}>Address</div>
                      <div style={{ display: "flex", alignItems: "flex-start", gap: 6, fontSize: 13 }}>
                        <MapPin size={13} style={{ color: "var(--text-muted)", flexShrink: 0, marginTop: 2 }} />
                        <span>{[d.address_line_1, d.address_line_2, d.city, d.state, d.pincode, d.country].filter(Boolean).join(", ")}</span>
                      </div>
                    </div>
                  )}
                  <div style={{ fontSize: 12, color: "var(--text-muted)", display: "flex", flexDirection: "column", gap: 3 }}>
                    {d.area && <span><b>Area:</b> {d.area.parent ? `${d.area.parent.name} › ` : ""}{d.area.name}</span>}
                    {d.crmUser && <span><b>CRM:</b> {d.crmUser.name}</span>}
                    {d.salesman && <span><b>Salesman:</b> {d.salesman.name}</span>}
                    {d.teamLead && <span><b>Team Lead:</b> {d.teamLead.name}</span>}
                  </div>
                </div>
              </div>

              <div style={{ borderTop: "1px solid var(--border)", paddingTop: 16 }}>
                <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 12, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <Users size={15} /> Customers ({leads.length})
                  </div>
                  <button
                    className="btn btn-primary btn-sm"
                    style={{ display: "inline-flex", alignItems: "center", gap: 4, padding: "5px 10px", fontSize: 11 }}
                    onClick={() => onAddBranch(d)}
                  >
                    <Plus size={11} /> Add Customer
                  </button>
                </div>
                {leads.length === 0 ? (
                  <div style={{ textAlign: "center", padding: "20px 0", color: "var(--text-muted)", fontSize: 13 }}>No customers linked yet. Click "Add Customer" to create one.</div>
                ) : (
                  <>
                    <div style={{ overflowX: "auto", borderRadius: 10, border: "1px solid var(--border)" }}>
                      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
                        <thead>
                          <tr style={{ background: "var(--bg)", borderBottom: "2px solid var(--border)" }}>
                            {["#", "Name", "Phone", "Status", "Active", "Last Order", ""].map(h => (
                              <th key={h} style={{ padding: "8px 12px", textAlign: "left", fontWeight: 700, fontSize: 11, color: "var(--text-muted)", textTransform: "uppercase", whiteSpace: "nowrap" }}>{h}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {paginatedLeads.map((lead, i) => (
                            <tr key={lead.id} style={{ borderBottom: "1px solid var(--border)", background: i % 2 === 0 ? "var(--card)" : "var(--bg)" }}>
                              <td style={{ padding: "8px 12px", color: "var(--text-muted)", fontWeight: 600 }}>{startIndex + i + 1}</td>
                              <td style={{ padding: "8px 12px", fontWeight: 600 }}>{lead.name}</td>
                              <td style={{ padding: "8px 12px" }}>{lead.phone || <Dash />}</td>
                              <td style={{ padding: "8px 12px" }}><StatusFlag flag={lead.status_flag} /></td>
                              <td style={{ padding: "8px 12px" }}>
                                {lead.is_active !== false
                                  ? <span className="badge badge-success" style={{ fontSize: 10 }}>Active</span>
                                  : <span className="badge badge-danger" style={{ fontSize: 10 }}>Inactive</span>}
                              </td>
                              <td style={{ padding: "8px 12px", whiteSpace: "nowrap" }}>{fmtDate(lead.last_ordered_at)}</td>
                              <td style={{ padding: "8px 12px" }}>
                                <div style={{ display: "flex", gap: 5 }}>
                                  <button
                                    className="btn btn-outline btn-sm"
                                    style={{ display: "inline-flex", alignItems: "center", gap: 4, padding: "4px 8px", fontSize: 11, color: "#dc2626", borderColor: "#fecaca" }}
                                    onClick={() => handleUnlink(lead)}
                                    disabled={unlinkingId === lead.id}
                                    title="Remove customer from this organisation"
                                  >
                                    {unlinkingId === lead.id ? (
                                      <Loader2 size={11} className="spin" />
                                    ) : (
                                      <Trash2 size={11} />
                                    )}
                                    Remove
                                  </button>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    {totalLeadsPages > 1 && (
                      <div style={{ display: "flex", alignItems: "center", gap: 8, justifyContent: "center", marginTop: 12 }}>
                        <button
                          type="button"
                          className="btn btn-outline btn-sm"
                          style={{ display: "inline-flex", alignItems: "center", gap: 4, padding: "4px 8px", fontSize: 11 }}
                          disabled={leadsPage <= 1}
                          onClick={() => setLeadsPage(p => Math.max(1, p - 1))}
                        >
                          <ChevronLeft size={12} /> Prev
                        </button>
                        <span style={{ fontSize: 11, color: "var(--text-muted)" }}>
                          {leadsPage} / {totalLeadsPages}
                        </span>
                        <button
                          type="button"
                          className="btn btn-outline btn-sm"
                          style={{ display: "inline-flex", alignItems: "center", gap: 4, padding: "4px 8px", fontSize: 11 }}
                          disabled={leadsPage >= totalLeadsPages}
                          onClick={() => setLeadsPage(p => Math.min(totalLeadsPages, p + 1))}
                        >
                          Next <ChevronRight size={12} />
                        </button>
                      </div>
                    )}
                  </>
                )}
              </div>
            </>
          )}
        </div>

        {/* Footer (Static) */}
        <div style={{ padding: "14px 20px", borderTop: "1px solid var(--border)", display: "flex", justifyContent: "flex-end", flexShrink: 0 }}>
          <button className="btn btn-outline btn-sm" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>,
    document.body
  );
}

// ─── Form Modal (Admin only) ──────────────────────────────────────────────────
function OrgFormModal({ modal, areas, crmUsers, onClose, onSaved }) {
  // Prevent unused-vars warnings
  void crmUsers;

  const isEdit = modal.mode === "edit";
  const org = isEdit ? modal.org : null;

  // Derive initial parentAreaId from the org's existing area (to preserve on edit)
  const initParentAreaId = () => {
    if (!org?.area_id) return "";
    const a = areas.find(x => String(x.id) === String(org.area_id));
    if (!a) return "";
    return a.parent_id ? String(a.parent_id) : "";
  };

  const [parentAreaId, setParentAreaId] = useState(initParentAreaId);
  const hasInitializedRef = useRef(false);

  useEffect(() => {
    if (org?.area_id && areas.length > 0 && !hasInitializedRef.current) {
      const a = areas.find(x => String(x.id) === String(org.area_id));
      if (a) {
        const pId = a.parent_id ? String(a.parent_id) : "";
        setParentAreaId(pId);
        hasInitializedRef.current = true;
      }
    }
  }, [areas, org]);

  const [form, setForm] = useState({
    name: org?.name || "",
    email: org?.email || "",
    phone: org?.phone || "",
    gst_number: org?.gst_number || "",
    address_line_1: org?.address_line_1 || "",
    address_line_2: org?.address_line_2 || "",
    city: org?.city || "",
    state: org?.state || "",
    pincode: org?.pincode || "",
    country: org?.country || "India",
    is_active: org?.is_active !== false,
    area_id: org?.area_id ? String(org.area_id) : "",
    assigned_crm_user_id: org?.assigned_crm_user_id ? String(org.assigned_crm_user_id) : "",
    assigned_salesman_id: org?.assigned_salesman_id ? String(org.assigned_salesman_id) : "",
    assigned_tl_id: org?.assigned_tl_id ? String(org.assigned_tl_id) : "",
  });

  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { setErr("Name is required."); return; }
    setSaving(true); setErr("");
    const payload = {
      ...form,
      area_id: form.area_id || null,
      parent_area_id: parentAreaId || null,
      assigned_crm_user_id: form.assigned_crm_user_id || null,
      assigned_salesman_id: form.assigned_salesman_id || null,
      assigned_tl_id: form.assigned_tl_id || null,
    };
    try {
      if (isEdit) { await organisationsApi.update(org.id, payload); }
      else { await organisationsApi.create(payload); }
      onSaved();
    } catch (ex) {
      setErr(ex.message || "Save failed.");
    } finally {
      setSaving(false);
    }
  };

  const tf = (key, label, opts = {}) => (
    <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
      <label style={{ fontSize: 11, fontWeight: 600, color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: "0.05em" }}>
        {label}{opts.required && " *"}
      </label>
      <input
        className="form-control" style={{ fontSize: 13 }}
        value={form[key]}
        onChange={e => set(key, e.target.value)}
        placeholder={opts.placeholder || ""}
        required={opts.required}
      />
    </div>
  );

  return createPortal(
    <div style={{
      position: "fixed", inset: 0, zIndex: 1000,
      background: "rgba(15, 23, 42, 0.65)",
      backdropFilter: "blur(4px)",
      display: "flex", alignItems: "center", justifyContent: "center",
      padding: 16,
    }} onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={{
        background: "var(--card)", borderRadius: 16,
        width: "100%", maxWidth: 680, maxHeight: "calc(100vh - 40px)",
        display: "flex", flexDirection: "column",
        boxShadow: "0 20px 60px rgba(0,0,0,0.25)",
        border: "1px solid var(--border)",
        overflow: "hidden"
      }}>
        {/* Header (Static) */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "18px 20px", borderBottom: "1px solid var(--border)", flexShrink: 0 }}>
          <span style={{ fontWeight: 700, fontSize: 15 }}>
            {isEdit ? `Edit — ${org.name}` : "Add Organisation"}
          </span>
          <button onClick={onClose} style={{ background: "none", border: "none", cursor: "pointer", color: "var(--text-muted)", padding: 4 }}>
            <XCircle size={20} />
          </button>
        </div>

        {/* Scrollable Form */}
        <form onSubmit={handleSave} style={{ display: "flex", flexDirection: "column", flex: 1, overflow: "hidden" }}>
          <div style={{ padding: 20, overflowY: "auto", flex: 1 }}>
            {err && (
              <div style={{ marginBottom: 14, padding: "10px 14px", borderRadius: 8, background: "#fef2f2", border: "1px solid #fecaca", color: "#dc2626", fontSize: 13 }}>
                {err}
              </div>
            )}

            {/* Basic info */}
            <div style={{ fontSize: 12, fontWeight: 700, color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 10 }}>Basic Info</div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 16 }}>
              {tf("name", "Name", { required: true })}
              {tf("email", "Email", { placeholder: "email@example.com" })}
              {tf("phone", "Phone")}
              {tf("gst_number", "GST Number", { placeholder: "22AAAAA0000A1Z5" })}
            </div>
          </div>

          {/* Footer (Static) */}
          <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", padding: "14px 20px", borderTop: "1px solid var(--border)", flexShrink: 0 }}>
            <button type="button" className="btn btn-outline btn-sm" onClick={onClose} disabled={saving}>Cancel</button>
            <button type="submit" className="btn btn-primary btn-sm" disabled={saving} style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
              {saving ? <Loader2 size={14} className="spin" /> : <Save size={14} />}
              {saving ? "Saving…" : "Save"}
            </button>
          </div>
        </form>
      </div>
    </div>,
    document.body
  );
}

// ─── Link Customer / Leads Modal (Admin only) ──────────────────────────────────
function LinkLeadModal({ org, refreshTrigger, onClose, onLinked, onCreateNew }) {
  const [search, setSearch] = useState("");
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const LIMIT = 15;
  const debSearch = useDebounce(search, 300);

  useEffect(() => {
    setPage(1);
  }, [debSearch]);

  useEffect(() => {
    setLoading(true);
    leadsApi.getAll({ limit: LIMIT, page, search: debSearch || undefined })
      .then(res => {
        const rows = Array.isArray(res.data) ? res.data : (res.data?.data || res.data?.rows || res || []);
        setLeads(rows);
        const count = res.count ?? res.data?.count ?? rows.length;
        setTotalCount(count);
        setTotalPages(Math.ceil(count / LIMIT) || 1);
      })
      .catch(() => {
        setLeads([]);
        setTotalCount(0);
        setTotalPages(1);
      })
      .finally(() => setLoading(false));
  }, [debSearch, page, refreshTrigger]);

  const handleLink = async (lead) => {
    try {
      await leadsApi.update(lead.id, {
        organisation_id: org.id,
        area_id: org.area_id || lead.area_id || null,
        sales_id: org.assigned_salesman_id || lead.sales_id || null,
      });
      // Refresh local list of leads
      leadsApi.getAll({ limit: LIMIT, page, search: debSearch || undefined })
        .then(res => {
          const rows = Array.isArray(res.data) ? res.data : (res.data?.data || res.data?.rows || res || []);
          setLeads(rows);
          const count = res.count ?? res.data?.count ?? rows.length;
          setTotalCount(count);
          setTotalPages(Math.ceil(count / LIMIT) || 1);
        });
      onLinked();
    } catch (ex) {
      alert("Failed to link customer: " + ex.message);
    }
  };

  return createPortal(
    <div style={{
      position: "fixed", inset: 0, zIndex: 1150,
      background: "rgba(15, 23, 42, 0.65)",
      backdropFilter: "blur(4px)",
      display: "flex", alignItems: "center", justifyContent: "center",
      padding: 16,
    }} onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={{
        background: "var(--card)", borderRadius: 16,
        width: "100%", maxWidth: 640, maxHeight: "calc(100vh - 80px)",
        display: "flex", flexDirection: "column",
        boxShadow: "0 24px 64px rgba(0,0,0,0.3)",
        border: "1px solid var(--border)",
        overflow: "hidden"
      }}>
        {/* Header */}
        <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "16px 20px", borderBottom: "1px solid var(--border)", flexShrink: 0 }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 700, fontSize: 16 }}>Link Customer / Leads</div>
            <div style={{ fontSize: 12, color: "var(--text-muted)" }}>Search and link existing leads to {org.name}</div>
          </div>
          <button onClick={onClose} style={{ background: "none", border: "none", cursor: "pointer", color: "var(--text-muted)", padding: 4 }}>
            <XCircle size={22} />
          </button>
        </div>

        {/* Toolbar */}
        <div style={{ display: "flex", gap: 10, padding: "14px 20px", borderBottom: "1px solid var(--border)", alignItems: "center", flexShrink: 0 }}>
          <div style={{ position: "relative", flex: 1 }}>
            <Search size={14} style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)", color: "var(--text-muted)", pointerEvents: "none" }} />
            <input
              className="search-bar"
              style={{ width: "100%", paddingLeft: 32, paddingRight: search ? 30 : 10, height: 38, fontSize: 13, boxSizing: "border-box" }}
              value={search} onChange={e => setSearch(e.target.value)}
              placeholder="Search leads by name, phone, shop…"
            />
            {search && (
              <button onClick={() => setSearch("")} style={{ position: "absolute", right: 8, top: "50%", transform: "translateY(-50%)", background: "none", border: "none", cursor: "pointer", color: "var(--text-muted)", padding: 2 }}>
                <X size={13} />
              </button>
            )}
          </div>
          <button
            className="btn btn-primary"
            style={{ display: "inline-flex", alignItems: "center", gap: 6, height: 38, fontSize: 13, whiteSpace: "nowrap" }}
            onClick={onCreateNew}
          >
            <Plus size={14} /> Add Customer
          </button>
        </div>

        {/* Scrollable Table */}
        <div style={{ flex: 1, overflowY: "auto", padding: 20, display: "flex", flexDirection: "column", gap: 12 }}>
          {loading && leads.length === 0 ? (
            <div style={{ display: "flex", alignItems: "center", gap: 8, color: "var(--text-muted)", padding: "24px 0" }}>
              <Loader2 size={16} className="spin" /> Loading leads…
            </div>
          ) : leads.length === 0 ? (
            <div style={{ textAlign: "center", padding: "24px 0", color: "var(--text-muted)", fontSize: 13 }}>
              {search ? `No leads found for "${search}"` : "No leads available"}
            </div>
          ) : (
            <>
              <div style={{ overflowX: "auto", borderRadius: 10, border: "1px solid var(--border)", flexShrink: 0 }}>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                  <thead>
                    <tr style={{ background: "var(--bg)", borderBottom: "2px solid var(--border)" }}>
                      {["Name", "Phone", "Shop Name", "Current Org", "Action"].map(h => (
                        <th key={h} style={{ padding: "8px 12px", textAlign: "left", fontWeight: 700, fontSize: 11, color: "var(--text-muted)", textTransform: "uppercase", whiteSpace: "nowrap" }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {leads.map((l, i) => {
                      const isLinked = String(l.organisation_id) === String(org.id);
                      const currentOrgName = l.Organisation?.name || (l.organisation_id ? `Org #${l.organisation_id}` : "—");
                      return (
                        <tr key={l.id} style={{ borderBottom: "1px solid var(--border)", background: i % 2 === 0 ? "var(--card)" : "var(--bg)" }}>
                          <td style={{ padding: "8px 12px", fontWeight: 600 }}>{l.name}</td>
                          <td style={{ padding: "8px 12px" }}>{l.phone || "—"}</td>
                          <td style={{ padding: "8px 12px" }}>{l.shop_name || "—"}</td>
                          <td style={{ padding: "8px 12px", color: l.organisation_id ? "var(--text-main)" : "var(--text-muted)" }}>{currentOrgName}</td>
                          <td style={{ padding: "8px 12px" }}>
                            {isLinked ? (
                              <span style={{ color: "#059669", fontWeight: 600, fontSize: 12 }}>✓ Linked</span>
                            ) : (
                              <button
                                className="btn btn-outline btn-xs"
                                onClick={() => handleLink(l)}
                                style={{ padding: "4px 8px", fontSize: 11 }}
                              >
                                {l.organisation_id ? "Transfer" : "Link"}
                              </button>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Pagination controls */}
              {totalPages > 1 && (
                <div style={{ display: "flex", alignItems: "center", gap: 8, justifyContent: "center", marginTop: 8, flexShrink: 0 }}>
                  <button
                    className="btn btn-outline btn-sm"
                    style={{ display: "inline-flex", alignItems: "center", gap: 4, padding: "5px 10px" }}
                    disabled={page <= 1}
                    onClick={() => setPage(p => Math.max(1, p - 1))}
                  >
                    <ChevronLeft size={14} /> Prev
                  </button>
                  <span style={{ fontSize: 12, color: "var(--text-muted)" }}>
                    {page} / {totalPages}
                  </span>
                  <button
                    className="btn btn-outline btn-sm"
                    style={{ display: "inline-flex", alignItems: "center", gap: 4, padding: "5px 10px" }}
                    disabled={page >= totalPages}
                    onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                  >
                    Next <ChevronRight size={14} />
                  </button>
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div style={{ padding: "14px 20px", borderTop: "1px solid var(--border)", display: "flex", justifyContent: "flex-end", flexShrink: 0 }}>
          <button className="btn btn-outline btn-sm" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>,
    document.body
  );
}

// ─── AsyncSearchSelect for Customer Modal ──────────────────────────────────────
function AsyncSearchSelect({ value, onChange, placeholder, disabled, loadOptions, initialLabel }) {
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selLabel, setSelLabel] = useState(initialLabel || '');
  const wrapRef = useRef(null);
  const debRef = useRef(null);

  useEffect(() => { if (initialLabel) setSelLabel(initialLabel); }, [initialLabel]);
  useEffect(() => { if (!value) setSelLabel(''); }, [value]);

  useEffect(() => {
    const handler = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setIsOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  useEffect(() => {
    if (!isOpen) return;
    clearTimeout(debRef.current);
    debRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const list = await loadOptions(query);
        setOptions(list);
        if (value) {
          const found = list.find(o => o.value?.toString() === value?.toString());
          if (found) setSelLabel(found.label);
        }
      } catch (_) { setOptions([]); }
      finally { setLoading(false); }
    }, query ? 300 : 0);
    return () => clearTimeout(debRef.current);
  }, [query, isOpen]);

  const displayLabel = options.find(o => o.value?.toString() === value?.toString())?.label || selLabel;

  return (
    <div ref={wrapRef} style={{ position: 'relative' }}>
      <div
        className="form-control"
        style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: disabled ? 'not-allowed' : 'pointer', background: disabled ? '#f9fafb' : '', userSelect: 'none' }}
        onClick={() => !disabled && setIsOpen(o => !o)}
      >
        <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: displayLabel ? 'inherit' : '#9ca3af', fontSize: 13 }}>
          {displayLabel || placeholder}
        </span>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
          {value && !disabled && (
            <span style={{ color: '#9ca3af', fontSize: 11, cursor: 'pointer' }}
              onClick={e => { e.stopPropagation(); onChange(''); setSelLabel(''); setQuery(''); }}>✕</span>
          )}
          <svg width="11" height="11" viewBox="0 0 12 12" fill="none" style={{ color: '#9ca3af' }}>
            <path d="M2 4l4 4 4-4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </div>
      </div>

      {isOpen && !disabled && (
        <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 200, background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8, marginTop: 4, boxShadow: '0 8px 24px rgba(0,0,0,0.12)' }}>
          <div style={{ padding: '8px 8px 6px', borderBottom: '1px solid #f1f5f9' }}>
            <input autoFocus type="text" value={query} onChange={e => setQuery(e.target.value)}
              placeholder="Type to search…"
              style={{ width: '100%', padding: '6px 10px', fontSize: 13, border: '1px solid #e2e8f0', borderRadius: 6, outline: 'none', boxSizing: 'border-box' }}
              onClick={e => e.stopPropagation()} />
          </div>
          <div style={{ maxHeight: 220, overflowY: 'auto', padding: '4px 0' }}>
            {loading ? (
              <div style={{ padding: '10px 14px', color: '#94a3b8', fontSize: 13 }}>Loading…</div>
            ) : options.length === 0 ? (
              <div style={{ padding: '10px 14px', color: '#94a3b8', fontSize: 13 }}>No results found</div>
            ) : options.map(opt => (
              <div key={opt.value}
                onClick={() => { onChange(opt.value); setSelLabel(opt.label); setIsOpen(false); setQuery(''); }}
                style={{ padding: '9px 14px', cursor: 'pointer', fontSize: 13, background: opt.value?.toString() === value?.toString() ? '#eff6ff' : 'transparent', color: opt.value?.toString() === value?.toString() ? '#1d4ed8' : 'inherit' }}
                onMouseEnter={e => { if (opt.value?.toString() !== value?.toString()) e.currentTarget.style.background = '#f8fafc'; }}
                onMouseLeave={e => { e.currentTarget.style.background = opt.value?.toString() === value?.toString() ? '#eff6ff' : 'transparent'; }}
              >
                {opt.label}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Add Branch / Customer Modal (Admin only) ──────────────────────────────────
function AddBranchModal({ org, onClose, onSaved }) {
  const [form, setForm] = useState({
    name: '',
    email: '',
    phone: '',
    parentAreaId: '',
    area_id: org.area_id ? String(org.area_id) : '',
    shopName: org.name || '',
    gst: org.gst_number || '',
    type: 'Retail',
    category: 'general',
    notes: '',
    address: [org.address_line_1, org.address_line_2, org.city, org.state, org.pincode].filter(Boolean).join(", ") || '',
    orgId: org.id ? String(org.id) : '',
    status: 'Active',
    salesPersonId: org.assigned_salesman_id ? String(org.assigned_salesman_id) : '',
    crmPersonId: org.assigned_crm_user_id ? String(org.assigned_crm_user_id) : '',
  });

  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  // Pre-populate parentAreaId when adding customer that has an area
  useEffect(() => {
    if (!org?.area_id) return;
    areasApi.getById(org.area_id)
      .then(res => {
        const area = res.data ?? res;
        if (area.parent_id) {
          setForm(f => ({ ...f, parentAreaId: String(area.parent_id) }));
        } else {
          setForm(f => ({ ...f, parentAreaId: String(area.id), area_id: '' }));
        }
      })
      .catch(() => { });
  }, [org.area_id]);

  const hc = (k, v) => setForm(f => ({ ...f, [k]: v }));

  // Reset child when parent area changes
  const prevParent = useRef(form.parentAreaId);
  useEffect(() => {
    if (prevParent.current !== form.parentAreaId) {
      prevParent.current = form.parentAreaId;
      setForm(f => ({ ...f, area_id: '', salesPersonId: '' }));
    }
  }, [form.parentAreaId]);

  // Reset sales person when area changes
  const prevArea = useRef(form.area_id);
  useEffect(() => {
    if (prevArea.current !== form.area_id) {
      prevArea.current = form.area_id;
      setForm(f => ({ ...f, salesPersonId: '' }));
    }
  }, [form.area_id]);

  /* ── Loaders ── */
  const loadOrgs = async (search) => {
    const res = await crmApiInstance.get('/leads/organisations', { params: { limit: 20, sortBy: 'name', sortOrder: 'ASC', ...(search ? { search } : {}) } });
    return (res.data?.data || res.data?.rows || []).map(o => ({ value: String(o.id), label: o.name }));
  };

  const loadParentAreas = async (search) => {
    const res = await areasApi.getAll({ parent_id: 'null', limit: 20, ...(search ? { search } : {}) });
    const d = res.data ?? res;
    return (Array.isArray(d) ? d : d.data ?? []).map(a => ({ value: String(a.id), label: a.name }));
  };

  const loadAreas = async (search) => {
    if (!form.parentAreaId) return [];
    const res = await areasApi.getAll({ parent_id: form.parentAreaId, limit: 20, ...(search ? { search } : {}) });
    const d = res.data ?? res;
    return (Array.isArray(d) ? d : d.data ?? []).map(a => ({ value: String(a.id), label: a.name }));
  };

  const loadStaffs = async (search) => {
    const effectiveArea = form.area_id || form.parentAreaId;
    if (!effectiveArea) return [];
    const res = await usersApi.getAll({ area_id: effectiveArea, limit: 20, ...(search ? { search } : {}) });
    const d = res.data ?? res;
    return (Array.isArray(d) ? d : d.data ?? []).map(s => ({ value: String(s.id), label: s.name }));
  };

  const loadCrmUsers = async (search) => {
    const res = await crmApiInstance.get('/admin/crm-users', { params: search ? { search } : {} });
    const d = res.data?.data || res.data || [];
    return (Array.isArray(d) ? d : []).map(u => ({ value: String(u.id), label: u.name }));
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.name?.trim()) { setErr('Full Name is required.'); return; }
    if (form.email && !form.email.match(/^[^\s@]+@[^\s@]+\.[^\s@]+$/)) { setErr('Valid email required.'); return; }
    if (!form.phone?.match(/^[6-9]\d{9}$/)) { setErr('Valid 10-digit phone required.'); return; }
    if (!form.shopName?.trim()) { setErr('Shop / Company Name is required.'); return; }
    if (!form.address?.trim()) { setErr('Address is required.'); return; }
    if (form.gst && !form.gst.match(/^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/)) { setErr('Invalid GST format.'); return; }

    setSaving(true); setErr('');
    const payload = {
      name: form.name.trim(),
      email: form.email.trim() || undefined,
      phone: form.phone.trim(),
      shop_name: form.shopName.trim(),
      gst_number: form.gst.trim() || "",
      customer_type: form.type || "Retail",
      customer_category: form.category || "general",
      notes: form.notes.trim() || "",
      address: form.address.trim(),
      status_flag: "yellow",
      is_active: form.status === 'Active',
      organisation_id: form.orgId ? parseInt(form.orgId, 10) : org.id,
      area_id: form.area_id || form.parentAreaId ? parseInt(form.area_id || form.parentAreaId, 10) : undefined,
      sales_id: form.salesPersonId ? parseInt(form.salesPersonId, 10) : undefined,
      assigned_to: form.crmPersonId ? parseInt(form.crmPersonId, 10) : null,
    };

    try {
      await leadsApi.create(payload);
      onSaved();
    } catch (ex) {
      setErr(ex.message || "Failed to create customer.");
      setSaving(false);
    }
  };

  const SECTION = { marginBottom: 10, fontSize: 12, fontWeight: 600, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em' };

  return createPortal(
    <div className="modal-overlay" onClick={onClose} style={{ zIndex: 1200 }}>
      <div className="modal" onClick={e => e.stopPropagation()} style={{ width: '640px', maxWidth: '92vw', maxHeight: '92vh', overflowY: 'auto' }}>
        <div className="modal-header">
          <span style={{ fontWeight: 700 }}>Add Customer to {org.name}</span>
          <button className="close-btn" onClick={onClose}>✕</button>
        </div>
        <div className="modal-body">
          {err && <div style={{ marginBottom: 12, padding: '8px 12px', borderRadius: 6, background: '#fef2f2', border: '1px solid #fecaca', color: '#dc2626', fontSize: 13 }}>{err}</div>}

          <div style={SECTION}>Personal Information</div>
          <div className="grid-2" style={{ marginBottom: 20 }}>
            <div className="form-group">
              <label className="form-label">Full Name *</label>
              <input className="form-control" value={form.name} onChange={e => hc('name', e.target.value)} placeholder="Enter full name" />
            </div>
            <div className="form-group">
              <label className="form-label">Email Address</label>
              <input className="form-control" value={form.email} onChange={e => hc('email', e.target.value)} placeholder="email@example.com" />
            </div>
            <div className="form-group">
              <label className="form-label">Phone Number *</label>
              <input className="form-control" value={form.phone} onChange={e => hc('phone', e.target.value)} placeholder="10-digit mobile number" maxLength={10} />
            </div>
            <div className="form-group">
              <label className="form-label">Customer Type</label>
              <select className="form-control" value={form.type} onChange={e => hc('type', e.target.value)}>
                {['Retail', 'Wholesale', 'Distributor', 'Corporate'].map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Customer Category</label>
              <select className="form-control" value={form.category} onChange={e => hc('category', e.target.value)}>
                {[['general', 'General Customer'], ['ecommerce', 'Ecommerce'], ['tender', 'Tender']].map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </div>
          </div>

          <div style={SECTION}>Business Information</div>
          <div className="grid-2" style={{ marginBottom: 20 }}>
            <div className="form-group">
              <label className="form-label">Shop / Company Name *</label>
              <input className="form-control" value={form.shopName} onChange={e => hc('shopName', e.target.value)} placeholder="Enter shop or company name" />
            </div>
            <div className="form-group">
              <label className="form-label">GST Number</label>
              <input className="form-control" value={form.gst} onChange={e => hc('gst', e.target.value.toUpperCase())} placeholder="29ABCDE1234F1Z5" maxLength={15} />
            </div>
            <div className="form-group">
              <label className="form-label">Organisation</label>
              <AsyncSearchSelect value={form.orgId} onChange={v => hc('orgId', v)} placeholder="— Search organisation —" loadOptions={loadOrgs} initialLabel={org.name || ''} />
            </div>
            <div className="form-group">
              <label className="form-label">Region / Parent Area</label>
              <AsyncSearchSelect value={form.parentAreaId} onChange={v => hc('parentAreaId', v)} placeholder="— Search region —" loadOptions={loadParentAreas} initialLabel={org.area?.parent?.name || ''} />
            </div>
            <div className="form-group">
              <label className="form-label">Area</label>
              <AsyncSearchSelect
                key={`area-${form.parentAreaId}`}
                value={form.area_id}
                onChange={v => hc('area_id', v)}
                placeholder={form.parentAreaId ? '— Search area —' : '— Select Region first —'}
                disabled={!form.parentAreaId}
                loadOptions={loadAreas}
                initialLabel={org.area?.name || ''}
              />
            </div>
            <div className="form-group">
              <label className="form-label">Sales Person</label>
              <AsyncSearchSelect
                key={`staff-${form.area_id || form.parentAreaId}`}
                value={form.salesPersonId}
                onChange={v => hc('salesPersonId', v)}
                placeholder="— Search sales person —"
                loadOptions={loadStaffs}
                initialLabel={org.salesman?.name || ''}
              />
            </div>
            <div className="form-group">
              <label className="form-label">CRM Person</label>
              <AsyncSearchSelect
                value={form.crmPersonId}
                onChange={v => hc('crmPersonId', v)}
                placeholder="— Search CRM user —"
                initialLabel={org.crmUser?.name || ''}
                loadOptions={loadCrmUsers}
              />
            </div>
          </div>

          <div style={SECTION}>Address &amp; Notes</div>
          <div style={{ display: 'grid', gap: 16, marginBottom: 20 }}>
            <div className="form-group">
              <label className="form-label">Full Address *</label>
              <textarea className="form-control" value={form.address} onChange={e => hc('address', e.target.value)} placeholder="Enter complete address with city and PIN code" rows={2} />
            </div>
            <div className="form-group">
              <label className="form-label">Notes</label>
              <textarea className="form-control" value={form.notes} onChange={e => hc('notes', e.target.value)} placeholder="Any special instructions or notes…" rows={2} />
            </div>
            <div className="form-group" style={{ maxWidth: 200 }}>
              <label className="form-label">Status</label>
              <select className="form-control" value={form.status} onChange={e => hc('status', e.target.value)}>
                <option>Active</option><option>Inactive</option>
              </select>
            </div>
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-outline" onClick={onClose} disabled={saving}>Cancel</button>
          <button className="btn btn-primary" onClick={handleSave} disabled={saving}>{saving ? 'Saving…' : 'Save Customer'}</button>
        </div>
      </div>
    </div>,
    document.body
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────
export default function OrganisationsPage({ user }) {
  const navigate = useNavigate();
  const isAdmin = user?.role === "admin";

  const [orgs, setOrgs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [addBranchOrg, setAddBranchOrg] = useState(null);
  const [linkLeadOrg, setLinkLeadOrg] = useState(null);
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  const handleBranchSaved = () => {
    setAddBranchOrg(null);
    setRefreshTrigger(prev => prev + 1);
    load();
  };
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [limit, setLimit] = useState(15);


  const [modal, setModal] = useState(null);
  const [detailOrg, setDetail] = useState(null);
  const [areas, setAreas] = useState([]);
  const [allUsers, setAllUsers] = useState([]);

  const debSearch = useDebounce(search, 350);

  const load = useCallback(() => {
    setLoading(true);
    organisationsApi
      .getAll({ page, limit: limit === 100000 ? 100000 : limit, search: debSearch || undefined })
      .then(res => {
        const rows = Array.isArray(res.data) ? res.data : (res.data?.data || res.data?.rows || []);
        setOrgs(rows);
        setTotalPages(res.totalPages || res.data?.totalPages || 1);
        setTotal(res.total || res.data?.total || rows.length);
      })
      .catch(() => setOrgs([]))
      .finally(() => setLoading(false));
  }, [page, debSearch, limit]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { setPage(1); }, [debSearch, limit]);

  const loadDropdowns = useCallback(() => {
    if (!isAdmin || (areas.length > 0 && allUsers.length > 0)) return;
    Promise.all([
      areasApi.getAll({ limit: 500 }),
      usersApi.getAll({ limit: 500 }),
    ]).then(([ar, us]) => {
      const areaRows = Array.isArray(ar.data) ? ar.data : (ar.data?.data || ar.data?.areas || []);
      const userRows = Array.isArray(us.data) ? us.data : (us.data?.data || us.data?.users || []);
      setAreas(areaRows);
      setAllUsers(userRows);
    }).catch(() => { });
  }, [isAdmin, areas.length, allUsers.length]);

  const openAdd = () => { loadDropdowns(); setModal({ mode: "add" }); };
  const openEdit = (org) => { loadDropdowns(); setModal({ mode: "edit", org }); };

  const handleSaved = () => { setModal(null); load(); };
  const handleDelete = async (org) => {
    if (!window.confirm(`Delete "${org.name}"? This cannot be undone.`)) return;
    try { await organisationsApi.remove(org.id); load(); }
    catch (ex) { alert(ex.message || "Delete failed."); }
  };

  // CRM users — not area-specific, always loaded from allUsers
  const crmUsers = allUsers.filter(u => ["crm", "crm_manager"].includes(getRoleName(u)));

  return (
    <div style={{ padding: 16 }}>
      {/* Toolbar */}
      <div style={{ display: "flex", gap: 10, marginBottom: 12, alignItems: "center", flexWrap: "wrap" }}>
        <div style={{ position: "relative", flex: 1, minWidth: 200 }}>
          <Search size={14} style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)", color: "var(--text-muted)", pointerEvents: "none" }} />
          <input
            className="search-bar"
            style={{ width: "100%", paddingLeft: 32, paddingRight: search ? 30 : 10, height: 38, fontSize: 13, boxSizing: "border-box" }}
            value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Search by name, phone, GST, city…"
          />
          {search && (
            <button onClick={() => setSearch("")} style={{ position: "absolute", right: 8, top: "50%", transform: "translateY(-50%)", background: "none", border: "none", cursor: "pointer", color: "var(--text-muted)", padding: 2 }}>
              <X size={13} />
            </button>
          )}
        </div>
        {isAdmin && (
          <button className="btn btn-primary btn-sm" style={{ display: "inline-flex", alignItems: "center", gap: 6, whiteSpace: "nowrap" }} onClick={openAdd}>
            <Plus size={14} /> Add Organisation
          </button>
        )}
      </div>

      {!loading && total > 0 && (
        <div style={{ fontSize: 12, color: "var(--text-muted)", marginBottom: 8 }}>
          {total} organisation{total !== 1 ? "s" : ""}
        </div>
      )}

      {/* List */}
      {loading ? (
        <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "40px 0", color: "var(--text-muted)", fontSize: 13 }}>
          <Loader2 size={18} className="spin" /> Loading…
        </div>
      ) : orgs.length === 0 ? (
        <div style={{ textAlign: "center", padding: "48px 0", color: "var(--text-muted)", fontSize: 13 }}>
          {search ? `No results for "${search}"` : "No organisations yet"}
        </div>
      ) : (
        /* ── Desktop table for admin / sales_tl desktop ── */
        <div style={{ overflowX: "auto", borderRadius: 12, border: "1px solid var(--border)", boxShadow: "var(--shadow)" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
            <thead>
              <tr style={{ background: "var(--card)", borderBottom: "2px solid var(--border)" }}>
                {["#", "Name", "Phone", "GST Number", "Status", "Created", "Actions"].map(h => {
                  const widths = {
                    "#": "50px",
                    "Name": "auto",
                    "Phone": "150px",
                    "GST Number": "160px",
                    "Status": "110px",
                    "Created": "120px",
                    "Actions": "220px",
                  };
                  return (
                    <th key={h} style={{padding: "10px 14px",textAlign: "left",fontWeight: 700,fontSize: 11,color: "var(--text-muted)",textTransform: "uppercase",letterSpacing: "0.05em",whiteSpace: "nowrap",width: widths[h] || "auto"}}>{h}</th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {orgs.map((org, idx) => (
                <tr key={org.id} style={{ borderBottom: "1px solid var(--border)", background: idx % 2 === 0 ? "var(--card)" : "var(--bg)" }}>
                  <td style={{ padding: "10px 14px", color: "var(--text-muted)", fontWeight: 600 }}>{org.id}</td>
                  <td style={{ padding: "10px 14px", whiteSpace: "nowrap" }}>
                    <button
                      onClick={() => setDetail(org)}
                      style={{
                        background: "none",
                        border: "none",
                        padding: 0,
                        font: "inherit",
                        fontWeight: 700,
                        color: "var(--text)",
                        cursor: "pointer",
                        textAlign: "left"
                      }}
                      onMouseEnter={e => e.currentTarget.style.color = "var(--primary)"}
                      onMouseLeave={e => e.currentTarget.style.color = "var(--text)"}
                    >
                      {org.name}
                    </button>
                  </td>
                  <td style={{ padding: "10px 14px", whiteSpace: "nowrap" }}>{org.phone || <Dash />}</td>
                  <td style={{ padding: "10px 14px", fontFamily: "monospace", fontSize: 12, whiteSpace: "nowrap" }}>{org.gst_number || <Dash />}</td>
                  <td style={{ padding: "10px 14px" }}>
                    {org.is_active === false
                      ? <span className="badge badge-danger" style={{ fontSize: 10 }}>Inactive</span>
                      : <span className="badge badge-success" style={{ fontSize: 10 }}>Active</span>}
                  </td>
                  <td style={{ padding: "10px 14px", whiteSpace: "nowrap", fontSize: 12 }}>{fmtDate(org.created_at)}</td>
                  <td style={{ padding: "10px 14px", whiteSpace: "nowrap" }}>
                    <div style={{ display: "flex", gap: 5, alignItems: "center" }}>
                      <button className="btn btn-outline btn-sm" style={{ display: "inline-flex", alignItems: "center", gap: 4, padding: "5px 9px" }} onClick={() => setDetail(org)} title="View connected branches">
                        <Building2 size={13} />
                      </button>
                      <button className="btn btn-outline btn-sm" style={{ display: "inline-flex", alignItems: "center", padding: "5px 9px" }} onClick={() => openEdit(org)} title="Edit">
                        <Pencil size={13} />
                      </button>
                      <button className="btn btn-outline btn-sm" style={{ display: "inline-flex", alignItems: "center", padding: "5px 9px", color: "#dc2626", borderColor: "#fecaca" }} onClick={() => handleDelete(org)} title="Delete">
                        <Trash2 size={13} />
                      </button>
                      <button className="btn btn-primary btn-sm" style={{ display: "inline-flex", alignItems: "center", gap: 4, padding: "5px 9px" }} onClick={() => navigate(`/place-order?org_id=${org.id}&org_name=${encodeURIComponent(org.name)}`)} title="Place Order">
                        <ShoppingCart size={13} /> Place order
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div style={{ display: "flex", alignItems: "center", gap: 8, justifyContent: "center", marginTop: 16, flexWrap: "wrap" }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <span style={{ fontSize: 13, color: '#475569', fontWeight: 500 }}>Show:</span>
            <select
              className="form-control"
              style={{ width: 'auto', padding: '4px 8px', fontSize: 13, cursor: 'pointer', height: 'auto' }}
              value={limit === 100000 ? 'all' : limit}
              onChange={e => { const v = e.target.value; setLimit(v === 'all' ? 100000 : parseInt(v, 10)); setPage(1); }}
            >
              <option value={15}>15</option>
              <option value={100}>100</option>
              <option value={200}>200</option>
              <option value={500}>500</option>
              <option value="all">All</option>
            </select>
          </div>
          <button className="btn btn-outline btn-sm" style={{ display: "inline-flex", alignItems: "center", gap: 4 }} disabled={page <= 1} onClick={() => setPage(p => Math.max(1, p - 1))}>
            <ChevronLeft size={14} /> Prev
          </button>
          <span style={{ fontSize: 12, color: "var(--text-muted)" }}>{page} / {totalPages}</span>
          <button className="btn btn-outline btn-sm" style={{ display: "inline-flex", alignItems: "center", gap: 4 }} disabled={page >= totalPages} onClick={() => setPage(p => Math.min(totalPages, p + 1))}>
            Next <ChevronRight size={14} />
          </button>
        </div>
      )}

      {detailOrg && (
        <OrgDetailsModal
          org={detailOrg}
          refreshTrigger={refreshTrigger}
          onClose={() => setDetail(null)}
          onAddBranch={(org) => setLinkLeadOrg(org)}
          onRefresh={() => {
            setRefreshTrigger(prev => prev + 1);
            load();
          }}
        />
      )}

      {linkLeadOrg && (
        <LinkLeadModal
          org={linkLeadOrg}
          refreshTrigger={refreshTrigger}
          onClose={() => setLinkLeadOrg(null)}
          onLinked={() => {
            setRefreshTrigger(prev => prev + 1);
            load();
          }}
          onCreateNew={() => {
            setAddBranchOrg(linkLeadOrg);
            setLinkLeadOrg(null);
          }}
        />
      )}

      {addBranchOrg && (
        <AddBranchModal
          org={addBranchOrg}
          onClose={() => setAddBranchOrg(null)}
          onSaved={handleBranchSaved}
        />
      )}

      {modal && isAdmin && (
        <OrgFormModal
          modal={modal}
          areas={areas}
          crmUsers={crmUsers}
          onClose={() => setModal(null)}
          onSaved={handleSaved}
        />
      )}
    </div>
  );
}
